import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtEur, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Target, Pencil } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

const NAVY = "#1B2A4A";
const AMBER = "#F5A623";

export default function Forecast() {
  const { t, isManager } = useApp();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState("all");
  const [editOpen, setEditOpen] = useState(false);
  const [editRep, setEditRep] = useState("");
  const [editTargets, setEditTargets] = useState({});

  const load = useCallback(() => api.get("/forecast").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const series = useMemo(() => {
    if (!data) return null;
    if (sel === "all") {
      const months = data.reps[0]?.months.map((m) => ({ ...m })) || [];
      data.reps.slice(1).forEach((r) => r.months.forEach((m, i) => {
        ["target", "actual", "cum_target", "cum_actual"].forEach((k) => { months[i][k] += m[k]; });
      }));
      const tt = data.reps.reduce((s, r) => s + r.total_target, 0);
      const ta = data.reps.reduce((s, r) => s + r.total_actual, 0);
      return { months, total_target: tt, total_actual: ta, attainment: tt ? Math.round(ta / tt * 1000) / 10 : 0 };
    }
    const rep = data.reps.find((r) => r.rep_id === sel);
    return rep ? { months: rep.months, total_target: rep.total_target, total_actual: rep.total_actual, attainment: rep.attainment } : null;
  }, [data, sel]);

  const openEditor = async (repId) => {
    const rid = repId || data?.reps[0]?.rep_id;
    if (!rid) return;
    setEditRep(rid);
    const rep = data.reps.find((r) => r.rep_id === rid);
    setEditTargets(Object.fromEntries(rep.months.map((m) => [m.month, m.target])));
    setEditOpen(true);
  };

  const switchEditRep = (rid) => {
    setEditRep(rid);
    const rep = data.reps.find((r) => r.rep_id === rid);
    setEditTargets(Object.fromEntries(rep.months.map((m) => [m.month, m.target])));
  };

  const saveTargets = async () => {
    const rep = data.reps.find((r) => r.rep_id === editRep);
    try {
      await api.put("/targets", {
        targets: Object.entries(editTargets).map(([month, v]) => ({
          rep_id: editRep, rep_name: rep.rep_name, territory: rep.territory, month, target_amount: Number(v) || 0,
        })),
      });
      toast.success(t("save") + " ✓");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  if (!data || !series) return <p className="text-gray-400">{t("loading")}</p>;

  const chartMonths = series.months.map((m) => ({ ...m, label: m.month.slice(5) }));

  return (
    <div className="space-y-5" data-testid="forecast-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t("forecast")} {data.year}</h1>
        <div className="flex items-center gap-2">
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger className="w-52 h-9 bg-white" data-testid="forecast-rep-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_reps")}</SelectItem>
              {data.reps.map((r) => <SelectItem key={r.rep_id} value={r.rep_id}>{r.rep_name} ({r.territory})</SelectItem>)}
            </SelectContent>
          </Select>
          {isManager && (
            <Button variant="outline" onClick={() => openEditor(sel !== "all" ? sel : null)} data-testid="edit-targets-btn">
              <Pencil className="h-3.5 w-3.5 mr-1.5" />{t("edit_targets")}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[[t("target"), fmtEur(series.total_target)], [t("actual"), fmtEur(series.total_actual)],
          [t("attainment"), `${series.attainment}%`]].map(([label, val], i) => (
          <div key={label} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`forecast-kpi-${i}`}>
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold flex items-center gap-1.5">
              <Target className="h-3.5 w-3.5" />{label}
            </p>
            <p className={`text-2xl font-bold font-mono mt-1 ${i === 2 ? (series.attainment >= 100 ? "text-emerald-600" : series.attainment >= 60 ? "text-[#F5A623]" : "text-red-500") : "text-[#1B2A4A]"}`}>{val}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h2 className="text-sm font-semibold mb-4">{t("monthly_comparison")}</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartMonths}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v) => fmtEur(v)} />
              <Legend />
              <Bar dataKey="target" name={t("target")} fill="#CBD5E1" radius={[3, 3, 0, 0]} />
              <Bar dataKey="actual" name={t("actual")} fill={AMBER} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h2 className="text-sm font-semibold mb-4">{t("burndown")}</h2>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartMonths}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v) => fmtEur(v)} />
              <Legend />
              <Line type="monotone" dataKey="cum_target" name={t("target")} stroke={NAVY} strokeWidth={2} dot={false} strokeDasharray="6 3" />
              <Line type="monotone" dataKey="cum_actual" name={t("actual")} stroke={AMBER} strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h2 className="text-sm font-semibold mb-3">{t("by_territory")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {data.territories.map((tr) => (
            <div key={tr.territory} className="border border-gray-100 rounded-md p-3" data-testid={`territory-${tr.territory}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">{tr.territory}</span>
                <Badge className={`${tr.attainment >= 100 ? "bg-emerald-100 text-emerald-700" : tr.attainment >= 60 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"} hover:bg-inherit`}>
                  {tr.attainment}%
                </Badge>
              </div>
              <p className="text-xs text-gray-500 mt-1 font-mono">{fmtEur(tr.total_actual)} / {fmtEur(tr.total_target)}</p>
              <div className="h-1.5 bg-gray-100 rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-[#F5A623] rounded-full" style={{ width: `${Math.min(tr.attainment, 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b bg-gray-50/60">
              <th className="px-4 py-2.5">{t("owner")}</th>
              <th className="px-4 py-2.5">{t("territory")}</th>
              <th className="px-4 py-2.5 text-right">{t("target")}</th>
              <th className="px-4 py-2.5 text-right">{t("actual")}</th>
              <th className="px-4 py-2.5 text-right">{t("attainment")}</th>
            </tr>
          </thead>
          <tbody>
            {data.reps.map((r) => (
              <tr key={r.rep_id} className="border-b border-gray-50" data-testid="forecast-rep-row">
                <td className="px-4 py-2.5 font-medium">{r.rep_name}</td>
                <td className="px-4 py-2.5 text-gray-500">{r.territory}</td>
                <td className="px-4 py-2.5 text-right font-mono">{fmtEur(r.total_target)}</td>
                <td className="px-4 py-2.5 text-right font-mono">{fmtEur(r.total_actual)}</td>
                <td className="px-4 py-2.5 text-right">
                  <Badge className={`${r.attainment >= 100 ? "bg-emerald-100 text-emerald-700" : r.attainment >= 60 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"} hover:bg-inherit font-mono`}>
                    {r.attainment}%
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("edit_targets")} {data.year}</DialogTitle></DialogHeader>
          <Select value={editRep} onValueChange={switchEditRep}>
            <SelectTrigger data-testid="edit-targets-rep-select"><SelectValue /></SelectTrigger>
            <SelectContent>{data.reps.map((r) => <SelectItem key={r.rep_id} value={r.rep_id}>{r.rep_name}</SelectItem>)}</SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2 max-h-[45vh] overflow-y-auto pr-1">
            {Object.entries(editTargets).map(([month, v]) => (
              <div key={month} className="flex items-center gap-2">
                <span className="text-xs font-mono text-gray-500 w-14">{month.slice(5)}/{month.slice(2, 4)}</span>
                <Input data-testid={`target-input-${month}`} type="number" className="h-8 font-mono text-xs" value={v}
                  onChange={(e) => setEditTargets((et) => ({ ...et, [month]: e.target.value }))} />
              </div>
            ))}
          </div>
          <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={saveTargets} data-testid="save-targets-btn">{t("save")}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
